import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { formatApiError } from '../../lib/errors';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, XCircle, Loader2, Tag } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMOJI_CHOICES = ['📦', '🕰️', '💍', '🎸', '📚', '🪑', '🔧', '🎨', '🌿', '👜', '⌚', '🏠', '🚲', '🐕', '🎮'];

function ClassifiedCategoriesTab({ language }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [busyId, setBusyId] = useState(null);
  const [emojiFor, setEmojiFor] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/classified-categories`, {
        params: filter !== 'all' ? { status: filter } : {},
      });
      setItems(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const setStatus = async (id, status) => {
    setBusyId(id);
    try {
      await axios.put(`${API}/admin/classified-categories/${id}`, { status, emoji: emojiFor[id] || undefined });
      await load();
    } catch (e) {
      alert(formatApiError(e, language === 'lv' ? 'Kļūda' : 'Error'));
    } finally { setBusyId(null); }
  };

  const L = (lv, en) => (language === 'lv' ? lv : en);
  const STATUS_COLOR = { pending: 'bg-amber-100 text-amber-800', approved: 'bg-green-100 text-green-800', rejected: 'bg-red-100 text-red-700' };

  return (
    <div className="bg-white border border-[#E5E4E0] rounded-2xl p-6" data-testid="admin-classified-categories-tab">
      <div className="flex items-center justify-between gap-4 flex-wrap mb-5">
        <div className="flex items-center gap-3">
          <Tag className="w-6 h-6 text-[#2F7E79]" />
          <h2 className="font-outfit text-xl font-bold text-[#121212]">{L('Sludinājumu kategorijas', 'Classified categories')}</h2>
        </div>
        <div className="flex gap-2 flex-wrap">
          {['pending', 'approved', 'rejected', 'all'].map((f) => (
            <button key={f} onClick={() => setFilter(f)}
              className={`text-xs px-3 py-1.5 rounded-full transition-colors ${filter === f ? 'bg-[#2F7E79] text-white' : 'bg-[#F3F2EE] text-[#5C5C5C] hover:bg-[#E6F2F0]'}`}
              data-testid={`cls-cat-filter-${f}`}>
              {{ pending: L('Gaida', 'Pending'), approved: L('Apstiprināts', 'Approved'), rejected: L('Noraidīts', 'Rejected'), all: L('Visi', 'All') }[f]}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="py-16 flex justify-center"><Loader2 className="w-7 h-7 animate-spin text-[#2F7E79]" /></div>
      ) : items.length === 0 ? (
        <p className="text-center text-[#5C5C5C] py-12" data-testid="cls-cat-empty">{L('Nav ierosinājumu.', 'No proposals.')}</p>
      ) : (
        <div className="space-y-3">
          {items.map((it) => (
            <div key={it.id} className="border border-[#E5E4E0] rounded-xl p-4" data-testid={`cls-cat-${it.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-xl">{emojiFor[it.id] || it.emoji || '📦'}</span>
                    <h3 className="font-semibold text-[#121212]">{it.lv}</h3>
                    <Badge className={`${STATUS_COLOR[it.status]} border-0 text-[11px]`}>{it.status}</Badge>
                  </div>
                  <p className="text-xs text-[#8A8A8A] mt-1">
                    {L('Slug', 'Slug')}: {it.slug}
                    {it.parent && <> · {L('virskategorija', 'parent')}: {it.parent}</>}
                    {' · '}{L('autors', 'by')}: {it.proposed_by_name || '—'}
                    {' · '}{it.pending_listings} {L('gaida sludinājumi', 'pending listings')}
                  </p>
                </div>
                {it.status === 'pending' && (
                  <div className="flex items-center gap-2">
                    <Button size="sm" onClick={() => setStatus(it.id, 'approved')} disabled={busyId === it.id}
                      className="bg-green-600 hover:bg-green-700 text-white gap-1" data-testid={`cls-cat-approve-${it.id}`}>
                      {busyId === it.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />} {L('Apstiprināt', 'Approve')}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setStatus(it.id, 'rejected')} disabled={busyId === it.id}
                      className="border-red-200 text-red-600 hover:bg-red-50 gap-1" data-testid={`cls-cat-reject-${it.id}`}>
                      <XCircle className="w-4 h-4" /> {L('Noraidīt', 'Reject')}
                    </Button>
                  </div>
                )}
              </div>
              {it.status === 'pending' && (
                <div className="mt-3 flex items-center gap-1.5 flex-wrap">
                  <span className="text-xs text-[#5C5C5C] mr-1">{L('Emocijzīme:', 'Emoji:')}</span>
                  {EMOJI_CHOICES.map((em) => (
                    <button key={em} onClick={() => setEmojiFor((m) => ({ ...m, [it.id]: em }))}
                      className={`w-8 h-8 rounded-lg text-base flex items-center justify-center transition-colors ${(emojiFor[it.id] || it.emoji) === em ? 'bg-[#2F7E79]/15 ring-2 ring-[#2F7E79]' : 'hover:bg-[#F3F2EE]'}`}
                      data-testid={`cls-cat-emoji-${it.id}-${em}`}>{em}</button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default ClassifiedCategoriesTab;
